<template>
	<div>
		<div class="flex justify-between">
			<div class="flex gap-2.5 text-sm text-gray-950">
				<button
					:class="{ 'text-blue-700': timeframe === '1D' }"
					@click="timeframe = '1D'"
				>
					1D
				</button>
				<button
					:class="{ 'text-blue-700': timeframe === '7D' }"
					@click="timeframe = '7D'"
				>
					7D
				</button>
				<button
					:class="{ 'text-blue-700': timeframe === '1M' }"
					@click="timeframe = '1M'"
				>
					1M
				</button>
				<button
					:class="{ 'text-blue-700': timeframe === '3M' }"
					@click="timeframe = '3M'"
				>
					3M
				</button>
				<button
					:class="{ 'text-blue-700': timeframe === 'YTD' }"
					@click="timeframe = 'YTD'"
				>
					YTD
				</button>
			</div>
			<div>
				<label for="dataType" class="hidden">Data type</label>
				<select class="text-sm text-gray-950 w-30 bg-transparent px-2 py-1" v-model="dataType" id="dataType">
					<option value="votes">VOTES</option>
					<option value="players">PLAYERS</option>
				</select>
			</div>
		</div>
		<div class="relative" style="height: 40vh;">
			<div id="players-chart"
			     style="width: 100%; height: 100%;"
			     :style="{opacity: data && chartState === ChartState.Loaded ? 1 : 0.25}"
			     class="players-chart"></div>
			<div class="absolute inset-0 flex items-center justify-center text-gray-950"
			     :style="{ display: chartState === ChartState.Loading ? 'flex' : 'none' }">
				Loading chart...
			</div>
			<div v-if="chartState === ChartState.NoData"
			     class="absolute inset-0 flex items-center justify-center text-gray-950">
				No data available for this timeframe
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
const echarts = ref(null);

const props = defineProps({
	server: {
		type: Object,
		required: true,
	},
});

const dataType = ref("players");
const timeframe = ref("7D");

watch([timeframe, dataType], async () => {
	if (!props.server) return;
	chartState.value = ChartState.Loading;
	fetchData();
});

watch(() => props.server, async () => {
	echarts.value = await import('echarts');
	fetchData();
}, {
	once: true
})

if (import.meta.server) {
	onMounted(() => {
		fetchData();
	});
}

enum ChartState {
	Loading,
	Loaded,
	Error,
	NoData
}

const data = ref(null);
const chartState = ref(ChartState.Loading);

function fetchData() {
	$fetch(`/api/server/${props.server._id}/chart`, {
		query: {
			timeframe: timeframe.value,
			datatype: dataType.value
		},
	}).then((res) => {
		if (res.empty) {
			chartState.value = ChartState.NoData;
		}
		data.value = res.data;

		updateChart(res.data);
	});
}

function updateChart(dataset) {
	if (!echarts.value || !dataset) return;
	let myChart = echarts.value.init(document.getElementById('players-chart'));

	requestIdleCallback(() => {
		myChart.setOption({
			tooltip: {
				trigger: 'axis',
				axisPointer: {type: 'cross'},
				formatter: function (params) {
					if (timeframe.value === '1D' || timeframe.value === '7D') {
						let format = "";
						for (let i = 0; i < params.length; i++) {
							const param = params[i];
							const val = param.data[i + 1];
							if (!val) continue;
							let date = new Date(param.value[0]).toLocaleDateString('en-US', {
								weekday: 'short',
								hour: 'numeric',
								minute: 'numeric',
							});
							if (param.seriesIndex == 1) {
								date = "Last " + date
							}
							format += `${params[i]?.marker ?? param.marker}${date}: ${val}<br>`;
						}
						return format;
					} else {
						let format = "Peak count<br>";
						for (let i = 0; i < params.length; i++) {
							const param = params[i];
							const val = param.data[i + 1];
							if (!val) continue;
							let date = new Date(param.value[0]).toLocaleDateString('en-US', {
								month: 'short',
								day: 'numeric',
							});
							if (param.seriesIndex == 1) {
								date = new Date(param.value[0]);
								if (timeframe.value === '1M') {
									date.setDate(date.getDate() - 28);
								} else if (timeframe.value === '3M') {
									date.setDate(date.getDate() - 84);
								} else if (timeframe.value === 'YTD') {
									date.setDate(date.getDate() - 364);
								}
								date = date.toLocaleDateString('en-US', {
									month: 'short',
									day: 'numeric',
								});
							}
							format += `${params[i]?.marker ?? param.marker}${date}: ${val}<br>`;
						}
						return format;
					}
				}
			},
			grid: {
				left: 0,
				top: 25,
				right: 0,
				bottom: 0,
				containLabel: true
			},
			legend: {
				orient: 'vertical',
				top: '0',
				left: '0',
			},
			dataset: {
				source: dataset
			},
			xAxis:
				[
					{
						type: 'category',
						axisLabel: {
							formatter: function (value) {
								const date = new Date(value);
								if (timeframe.value === '1D' || timeframe.value === '7D') {
									return date.toLocaleDateString('en-US', {
										weekday: 'short',
										hour: 'numeric',
									});
								} else {
									return date.toLocaleDateString('en-US', {
										month: 'short',
										day: 'numeric',
									});
								}
							}
						},
						axisPointer: {
							label: {
								show: true,
								formatter: function (params) {
									const date = new Date(params.value);
									if (timeframe.value === '1D' || timeframe.value === '7D') {
										return date.toLocaleDateString('en-US', {
											weekday: 'short',
											hour: 'numeric',
											minute: 'numeric',
										});
									} else {
										return date.toLocaleDateString('en-US', {
											month: 'short',
											day: 'numeric',
										});
									}
								}
							},
						},
					},
				],
			yAxis: {
				type: 'value',
				position: 'right',
			},
			series: [
				{
					type: 'line',
					name: dataset[0][1],
					color: '#3B82F6',
					showSymbol: true,
					lineStyle: {
						width: 4
					},
					connectNulls: true,
					itemStyle: {
						shadowBlur: 4,
						shadowColor: '#3B82F6',
						shadowOffsetY: 2,
						color: {
							type: 'radial',
							x: 0.4,
							y: 0.3,
							r: 1,
							colorStops: [
								{
									offset: 0,
									color: '#3B82F6'
								},
								{
									offset: 1,
									color: '#3B82F6'
								}
							]
						}
					},
					z: 2
				},
				{
					type: 'line',
					name: dataset[0][2],
					color: 'rgba(107, 114, 128, 0.5)',
					showSymbol: true,
					lineStyle: {
						width: 2
					},
					connectNulls: true,
					itemStyle: {
						shadowBlur: 4,
						shadowColor: '#6B7280',
						shadowOffsetY: 2,
						color: {
							type: 'radial',
							x: 0.4,
							y: 0.3,
							r: 1,
							colorStops: [
								{
									offset: 0,
									color: 'rgba(107, 114, 128, 0.5)'
								},
								{
									offset: 1,
									color: 'rgba(107, 114, 128, 0.5)'
								}
							]
						}
					},
					z: 1
				}
			],
		});

		if (chartState.value === ChartState.Loading) {
			chartState.value = ChartState.Loaded;
		}
	});
}

</script>